/**
 * Gate pass item rows.
 *
 * A line has one of two shapes, because both things really do cross the gate:
 *
 *   STORE  a row that exists in the selected store's stock — item + batch.
 *          grn_item_id is THE batch identity; batch_number is only its label,
 *          and is not unique across GRNs.
 *   OTHER  an off-master asset. The ward AC that has gone out for repair was
 *          never a stock row in any store, so there is no item to pick and no
 *          batch to name — it is a description plus the serial number of the
 *          physical piece that has to come back.
 *
 * A NON-RETURNABLE line is always STORE: something leaving for good has to name
 * the stock it came from. A RETURNABLE line picks per row, because the flagship
 * returnable case is exactly the off-master one.
 *
 * NEITHER SHAPE MOVES STOCK. GatePassService is explicit that a gate pass never
 * touches stock_balances — not returnable, not non-returnable, not at gate-out.
 * The item and batch on a line are traceability, not a posting key, which is why
 * quantity is never capped at on-hand: if 10 physically left and the books say
 * 5, the register still has to be able to say 10.
 *
 * On-hand is deliberately NOT shown. It was a column here and a number inside
 * every batch option; both are gone at the client's request, because a figure
 * that constrains nothing invited people to reconcile against it.
 */
import React from 'react';
import {
  Box, Table, TableHead, TableRow, TableCell, TableBody, IconButton, Tooltip, Typography, Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import StockBalanceAutocomplete from '../StockBalanceAutocomplete';
import InvButton from '../InvButton';
import { InvTextField, InvSelect } from '../InvField';
import { batchRowKey } from '../../utils/batchRowIdentity';
import useItemNames from '../../hooks/useItemNames';
import { GATE_PASS_LINE_TYPES, GATE_PASS_LINE_TYPE_OPTIONS } from '../../constants/gatePass';
import { formatDate } from '../../utils';
import { IPD_COLORS } from '../../theme';

export const GATE_PASS_LINE_DEFAULT = {
  line_type: GATE_PASS_LINE_TYPES.STORE,
  item_key: '',
  item_id: '',
  item_name: '',
  item_description: '',
  batch_number: '',
  grn_item_id: '',
  _row_key: '',
  _batches: [],
  expiry_date: '',
  uom_id: '',
  uom_code: '',
  serial_number: '',
  quantity: '',
  remarks: '',
};

export function toNumberOrNull(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function isStoreLine(line) {
  return (line?.line_type || GATE_PASS_LINE_TYPES.STORE) === GATE_PASS_LINE_TYPES.STORE;
}

/** Batch label — identity and expiry only. No quantity, by design (see header). */
export function batchOptionLabel(batch) {
  const label = batch?.batch_number || 'No batch';
  const expiry = batch?.expiry_date ? ` · Exp: ${formatDate(batch.expiry_date)}` : '';
  return `${label}${expiry}`;
}

/**
 * The item's Item Master classification, read-only, beside the item it belongs
 * to — "Tablets", "Capital Goods". It answers "what IS this thing" at the
 * barrier, which an item name alone often does not.
 *
 * Resolved from the item catalog by item_id rather than carried on the line,
 * because the line reaches this table three different ways — picked fresh from
 * the store catalogue, loaded from a saved draft, or copied off a Vendor Return
 * — and only item_id is present in all three. The catalog is one cached GET for
 * the whole inventory module.
 *
 * An unresolved class renders nothing rather than an empty pill.
 */
function ClassificationChip({ name }) {
  if (!name) return null;
  return (
    <Chip
      label={name}
      size="small"
      sx={{ height: 20, fontSize: 10, mt: 0.5 }}
    />
  );
}

export default function GatePassLineTable({
  lines, onChange, returnable, storeId, disabled, locked = false,
}) {
  const { getItemClass } = useItemNames();

  function updateLine(i, patch) {
    onChange(lines.map((line, idx) => (idx === i ? { ...line, ...patch } : line)));
  }

  function addLine() {
    onChange([...lines, { ...GATE_PASS_LINE_DEFAULT }]);
  }

  function removeLine(i) {
    onChange(lines.length === 1 ? [{ ...GATE_PASS_LINE_DEFAULT }] : lines.filter((_, idx) => idx !== i));
  }

  /**
   * Switching what the line IS invalidates everything that identified the old
   * shape — an item and a batch mean nothing on an off-master row, a serial
   * number means nothing on a stock row. Quantity and remarks survive, because
   * they are true of the material either way.
   */
  function changeLineType(i, lineType) {
    const line = lines[i];
    updateLine(i, {
      ...GATE_PASS_LINE_DEFAULT,
      line_type: lineType,
      quantity: line.quantity,
      remarks: line.remarks,
    });
  }

  function selectItem(i, key, item) {
    if (!item) {
      updateLine(i, { ...GATE_PASS_LINE_DEFAULT, line_type: lines[i].line_type });
      return;
    }
    updateLine(i, {
      item_key: key || item.item_id,
      item_id: item.item_id,
      item_name: item.item_name || item.item_code || '',
      // Quantity on this screen is in the item's BASE unit — the same
      // convention the discard and adjustment screens use.
      uom_id: item.base_uom_id || '',
      uom_code: item.base_uom_code || item.uom_name || '',
      batch_number: '',
      grn_item_id: '',
      _row_key: '',
      expiry_date: '',
      _batches: item.batches || [],
    });
  }

  // Keyed on the balance row, never on batch_number: most items are not
  // batch-tracked, so batch_number is NULL on every one of their rows while each
  // GRN receipt still has its own row and its own pack size.
  function selectBatch(i, rowKey) {
    const line = lines[i];
    const batch = (line._batches || []).find(b => batchRowKey(b) === rowKey);
    updateLine(i, {
      _row_key: rowKey,
      batch_number: batch?.batch_number || '',
      grn_item_id: batch?.grn_item_id || '',
      expiry_date: batch?.expiry_date || line.expiry_date || '',
    });
  }

  const headCellSx = { color: IPD_COLORS.textSecondary };
  const readOnlySx = { fontSize: 13, color: IPD_COLORS.textPrimary };

  return (
    <Box>
      <Box sx={{ overflowX: 'auto' }}>
        <Table
          size="small"
          sx={{ '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary } }}
        >
          <TableHead>
            <TableRow>
              {/* The per-row shape switch. Non-returnable has no choice to make. */}
              {returnable && !locked && (
                <TableCell sx={{ ...headCellSx, width: 140 }}>Type *</TableCell>
              )}
              <TableCell sx={{ ...headCellSx, minWidth: 260 }}>
                {returnable ? 'Item / equipment *' : 'Item *'}
              </TableCell>
              <TableCell sx={{ ...headCellSx, width: 210 }}>
                {returnable ? 'Batch no. / Serial no. *' : 'Batch no. *'}
              </TableCell>
              <TableCell sx={{ ...headCellSx, width: 110 }}>Qty *</TableCell>
              <TableCell sx={{ ...headCellSx, minWidth: 160 }}>Remarks</TableCell>
              {!locked && <TableCell sx={{ width: 56 }} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map((line, i) => {
              const storeLine = isStoreLine(line);
              return (
                <TableRow key={i}>
                  {returnable && !locked && (
                    <TableCell>
                      <InvSelect
                        label=""
                        value={line.line_type || GATE_PASS_LINE_TYPES.STORE}
                        onChange={e => changeLineType(i, e.target.value)}
                        options={GATE_PASS_LINE_TYPE_OPTIONS}
                        disabled={disabled}
                      />
                    </TableCell>
                  )}

                  {/* ── Item ─────────────────────────────────────────────── */}
                  <TableCell>
                    {!locked && storeLine && (
                      <StockBalanceAutocomplete
                        storeId={storeId}
                        value={line.item_key}
                        onChange={(key, item) => selectItem(i, key, item)}
                        placeholder="Search item…"
                        disabled={disabled || !storeId}
                        includeExpired
                        showAvailability={false}
                      />
                    )}
                    {!locked && !storeLine && (
                      <InvTextField
                        label=""
                        value={line.item_description}
                        onChange={e => updateLine(i, { item_description: e.target.value })}
                        placeholder="e.g. Split AC 1.5T, ward 3"
                        disabled={disabled}
                      />
                    )}
                    {locked && (
                      <Typography sx={readOnlySx}>
                        {line.item_name || line.item_description || '—'}
                      </Typography>
                    )}
                    <ClassificationChip name={getItemClass(line.item_id)} />
                  </TableCell>

                  {/* ── Batch (store line) or serial number (off-master) ──── */}
                  <TableCell>
                    {locked && (
                      <Typography sx={readOnlySx}>
                        {line.batch_number || line.serial_number || '—'}
                      </Typography>
                    )}
                    {!locked && storeLine && (
                      <InvSelect
                        label=""
                        value={line._row_key}
                        onChange={e => selectBatch(i, e.target.value)}
                        options={[
                          { value: '', label: '—' },
                          ...(line._batches || []).map(b => ({
                            value: batchRowKey(b),
                            label: batchOptionLabel(b),
                          })).filter(o => o.value),
                        ]}
                        disabled={disabled || !line.item_id}
                      />
                    )}
                    {!locked && !storeLine && (
                      <InvTextField
                        label=""
                        value={line.serial_number}
                        onChange={e => updateLine(i, { serial_number: e.target.value })}
                        placeholder="SN-44821"
                        disabled={disabled}
                      />
                    )}
                  </TableCell>

                  {/* ── Quantity ─────────────────────────────────────────── */}
                  <TableCell>
                    {locked ? (
                      <Typography sx={readOnlySx}>
                        {line.quantity}
                        {line.uom_code ? ` ${line.uom_code}` : ''}
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.6 }}>
                        <InvTextField
                          label=""
                          value={line.quantity}
                          onChange={e => updateLine(i, { quantity: e.target.value })}
                          type="number"
                          inputProps={{ min: 0.01, step: 0.01 }}
                          disabled={disabled}
                        />
                        {line.uom_code && (
                          <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary, pt: 1.2 }}>
                            {line.uom_code}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </TableCell>

                  {/* ── Remarks ──────────────────────────────────────────── */}
                  <TableCell>
                    {locked ? (
                      <Typography sx={readOnlySx}>{line.remarks || '—'}</Typography>
                    ) : (
                      <InvTextField
                        label=""
                        value={line.remarks}
                        onChange={e => updateLine(i, { remarks: e.target.value })}
                        placeholder={storeLine ? '' : 'e.g. Display fault'}
                        disabled={disabled}
                      />
                    )}
                  </TableCell>

                  {!locked && (
                    <TableCell>
                      <Tooltip title="Remove line">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => removeLine(i)}
                            disabled={disabled}
                            sx={{ color: IPD_COLORS.error.text }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      {!locked && (
        <Box sx={{ mt: 1.5 }}>
          <InvButton
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={addLine}
            disabled={disabled}
          >
            Add item
          </InvButton>
        </Box>
      )}
    </Box>
  );
}
